import React, { forwardRef, Children, KeyboardEvent, ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import { motion } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import { KeyNames } from '../../constants/key-names';
import type { PolymorphicRef, PolymorphicComponentPropWithRef } from '../../type-utils/polymorphic';
import { ContentColumn } from './atoms/content-column/list-row-content-column.component';
import { LeadingColumn } from './atoms/leading-column/list-row-leading-column';
import { TrailingColumn } from './atoms/trailing-column/list-row-trailing-column.component';
import { ListRowStyles } from './list-row.styles';

export type ListRowProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
  C,
  { isSelected?: boolean; trackingId?: string }
>;

type ListRowComponent = <C extends React.ElementType = 'li'>(props: ListRowProps<C>) => React.ReactNode;

export const ListRowOptional: ListRowComponent = forwardRef(
  <C extends React.ElementType = 'li'>(
    { children, as, isSelected = false, trackingId, ...rest }: ListRowProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const reactChildren: ReactNode[] = Children.toArray(children);
    const Component = as || 'li';
    const { onClick } = rest;
    const styles = ListRowStyles.Row({
      isClickable: !!onClick,
      isSelected,
    });

    const indicatorStyles = ListRowStyles.Indicator;

    const contentColumnComponent = reactChildren.filter(
      (child) => child?.['type']?.displayName === 'ContentColumn'
    )[0]?.['props'].children;

    return (
      <Component
        ref={ref}
        onClick={() => onClick?.()}
        aria-label={onlyText(contentColumnComponent as React.ReactNode) || 'List row'}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === KeyNames.Enter || e.key === KeyNames.Space) {
            onClick?.(e);
          }
        }}
        data-trackingid={trackingId}
        {...rest}
        className={cx(styles, 'list-row', rest.className)}
      >
        {children}
        {isSelected && (
          <motion.span
            layoutId='list-row-indicator'
            initial={false}
            layout='position'
            transition={{ ease: 'easeInOut', duration: 0.3 }}
            className={indicatorStyles}
          />
        )}
      </Component>
    );
  }
);

const ListRowNamespace = Object.assign(ListRowOptional, {
  Lead: LeadingColumn,
  Content: ContentColumn,
  Trail: TrailingColumn,
});

export { ListRowNamespace as ListRow };
